export type CategoryTabKey = 'printers' | 'scanners' | 'phones' | 'water-dispensers' | 'shredders' | 'other' | 'office-copiers'

export interface CategoryTabItem {
  key: CategoryTabKey
  label: string
}

export const CATEGORY_TABS: CategoryTabItem[] = [
  { key: 'printers', label: 'Printers & Copiers' },
  { key: 'scanners', label: 'Scanners' },
  { key: 'phones', label: 'Phones' },
  { key: 'water-dispensers', label: 'Water Dispensers' },
  { key: 'shredders', label: 'Shredders' },
]

/**
 * Determines whether a product classifies as a Phone (IP Phone).
 */
export function IsPhone(product: any): boolean {
  if (!product) return false
  return String(product.printer_category || '').trim().toLowerCase() === 'ip phone'
}

/**
 * Determines whether a product classifies as a dedicated Scanner.
 */
export function IsScanner(product: any): boolean {
  if (!product) return false
  return String(product.printer_category || '').trim().toLowerCase() === 'scanner'
}

/**
 * Determines whether a product classifies as a Water Dispenser.
 */
export function IsWaterDispenser(product: any): boolean {
  if (!product) return false
  return String(product.printer_category || '').trim().toLowerCase() === 'water dispenser'
}

/**
 * Determines whether a product classifies as a Paper Shredder.
 */
export function IsShredder(product: any): boolean {
  if (!product) return false
  return String(product.printer_category || '').trim().toLowerCase() === 'shredder'
}

/**
 * Determines whether a product classifies as an Office Copier / Multifunction Copier (MFP).
 * Grounded in catalog-server-v2 products table schema:
 * - printer_category = 'Printer'
 * - capability = 'Multifunction' or 'Copier-only'
 * - Or has explicit copy_speed / adf_pages / scanner_type (excluding dedicated single-function printers).
 */
export function IsOfficeCopier(product: any): boolean {
  if (!product) return false
  const cat = String(product.printer_category || '').trim()
  if (cat !== 'Printer') return false

  const cap = String(product.capability || '').trim().toLowerCase()
  if (cap === 'multifunction' || cap === 'copier-only') {
    return true
  }
  if (cap === 'single-function' || cap === 'label/receipt' || cap === 'photo-printing' || cap === 'scanner-only') {
    return false
  }

  // Fallback for models with unpopulated capability but documented scanner/copier engines
  const copy_speed = String(product.copy_speed || '').trim()
  const adf = String(product.adf_pages || '').trim()
  const scanner = String(product.scanner_type || '').trim()
  if ((copy_speed !== '' && copy_speed !== '0') || (adf !== '' && adf !== '0') || scanner !== '') {
    return true
  }

  return false
}

/**
 * Determines whether a product classifies as a Printer (Single-function, label, or production printer).
 */
export function IsPrinter(product: any): boolean {
  if (!product) return false
  const cat = String(product.printer_category || '').trim()
  if (cat !== 'Printer') return false
  return !IsOfficeCopier(product) && !IsScanner(product)
}

/**
 * Returns the matched CategoryTabKey for any product record.
 */
export function GetProductTabKey(product: any): CategoryTabKey {
  if (!product) return 'printers'
  if (IsWaterDispenser(product)) return 'water-dispensers'
  if (IsShredder(product)) return 'shredders'
  if (IsPhone(product)) return 'phones'
  if (IsScanner(product)) return 'scanners'
  return 'printers'
}

/**
 * Evaluates whether a product matches the selected active tab filter.
 */
export function FilterProductByTab(product: any, tab_key: CategoryTabKey | string | null | undefined): boolean {
  if (!tab_key) return true
  const raw = String(tab_key).trim().toLowerCase()
  const normalized_tab = (raw === 'office-copiers' || raw === 'other') ? 'printers'
    : (raw === 'water-dispenser' || raw === 'water-dispensers' || raw === 'water') ? 'water-dispensers'
    : (raw === 'shredder' || raw === 'shredders') ? 'shredders'
    : (raw === 'scanner' || raw === 'scanners') ? 'scanners'
    : (raw === 'phone' || raw === 'phones') ? 'phones'
    : raw
  return GetProductTabKey(product) === normalized_tab
}

/**
 * Sanitizes and resets filter parameters that are not applicable to the given tab key.
 */
export function SanitizeFiltersForTab(raw_filters: any, tab_key: CategoryTabKey | string): any {
  const raw = String(tab_key).trim().toLowerCase()
  const normalized_tab = (raw === 'office-copiers' || raw === 'other') ? 'printers'
    : (raw === 'water-dispenser' || raw === 'water-dispensers' || raw === 'water') ? 'water-dispensers'
    : (raw === 'shredder' || raw === 'shredders') ? 'shredders'
    : (raw === 'scanner' || raw === 'scanners') ? 'scanners'
    : (raw === 'phone' || raw === 'phones') ? 'phones'
    : raw as CategoryTabKey

  const updated = { ...raw_filters, category_tab: normalized_tab }

  // Reset manufacturer filter if changing to a category tab with disjoint brands
  if (raw_filters.category_tab && raw_filters.category_tab !== normalized_tab) {
    updated.manufacturers = []
  }

  if (normalized_tab === 'scanners') {
    updated.office_sizes = []
    updated.print_volumes = []
    updated.speed_minimum = null
    updated.speed_maximum = null
    updated.capabilities = []
    updated.product_categories = []
  } else if (normalized_tab === 'phones') {
    updated.office_sizes = []
    updated.print_volumes = []
    updated.copier_colors = []
    updated.speed_minimum = null
    updated.speed_maximum = null
    updated.form_factors = []
    updated.capabilities = []
    updated.paper_sizes = []
    updated.usage_levels = []
    updated.product_categories = []
  } else if (normalized_tab === 'water-dispensers' || normalized_tab === 'shredders' || normalized_tab === 'other') {
    updated.office_sizes = []
    updated.print_volumes = []
    updated.copier_colors = []
    updated.speed_minimum = null
    updated.speed_maximum = null
    updated.form_factors = []
    updated.capabilities = []
    updated.connection_types = []
    updated.paper_sizes = []
    updated.usage_levels = []
    updated.product_categories = []
  } else {
    // printers & copiers
    updated.product_categories = []
  }

  return updated
}
